import clsx from 'clsx';
import { __ } from '@wordpress/i18n';
import { settings } from '@wordpress/icons';
import { useState, forwardRef } from '@wordpress/element';
import { useInstanceId } from '@wordpress/compose';
import { Button } from '../button';
import RangeControl from '../range-control';
import { Flex, FlexItem } from '../flex';
import { default as UnitControl, parseQuantityAndUnitFromRawValue, useCustomUnits } from '../unit-control';
import BaseControl from '../base-control';
import { HStack } from '../h-stack';
import styles from './style.module.scss';
import { Spacer } from '../spacer';
import FontSizePickerSelect from './font-size-picker-select';
import FontSizePickerToggleGroup from './font-size-picker-toggle-group';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const DEFAULT_UNITS = ['px', 'em', 'rem', 'vw', 'vh'];
const MAX_TOGGLE_GROUP_SIZES = 5;
const UnforwardedFontSizePicker = (props, ref) => {
  const {
    fallbackFontSize,
    fontSizes = [],
    disableCustomFontSizes = false,
    onChange,
    units: unitsProp = DEFAULT_UNITS,
    value,
    valueMode = 'literal',
    withSlider = false,
    withReset = true
  } = props;
  const labelId = useInstanceId(UnforwardedFontSizePicker, 'font-size-picker-label');
  const units = useCustomUnits({
    availableUnits: unitsProp
  });
  const selectedFontSize = (() => {
    if (!value) {
      return undefined;
    }

    // If valueMode is 'slug', find by slug
    if (valueMode === 'slug') {
      return fontSizes.find(fontSize => fontSize.slug === value);
    }

    // If valueMode is 'literal', find by size value
    return fontSizes.find(fontSize => fontSize.size === value);
  })();
  const isCustomValue = !!value && !selectedFontSize;

  // Initially request a custom picker if the value is not from the predef list.
  const [userRequestedCustom, setUserRequestedCustom] = useState(isCustomValue);

  // Resolve the literal value to use in custom controls when operating in slug mode.
  // When `valueMode` is 'slug', the `value` prop contains the slug of the
  // selected preset. In that case, the custom input should reflect the preset's
  // actual size value so it pre-populates correctly after clicking "Set custom size".
  const resolvedValueForControls = valueMode === 'slug' ? selectedFontSize?.size : value;
  let currentPickerType;
  if (!disableCustomFontSizes && userRequestedCustom) {
    // While showing the custom value picker, switch back to predef only if
    // `disableCustomFontSizes` is set to `true`.
    currentPickerType = 'custom';
  } else {
    currentPickerType = fontSizes.length > MAX_TOGGLE_GROUP_SIZES ? 'select' : 'togglegroup';
  }
  if (fontSizes.length === 0 && disableCustomFontSizes) {
    return null;
  }

  // If neither the value or first font size is a string, then FontSizePicker
  // operates in a legacy "unitless" mode where UnitControl can only be used
  // to select px values and onChange() is always called with number values.
  const hasUnits = typeof resolvedValueForControls === 'string' || typeof fontSizes[0]?.size === 'string';
  const [valueQuantity, valueUnit] = parseQuantityAndUnitFromRawValue(resolvedValueForControls, units);
  const isValueUnitRelative = !!valueUnit && ['em', 'rem', 'vw', 'vh'].includes(valueUnit);
  const isDisabled = value === undefined;
  return /*#__PURE__*/_jsxs("fieldset", {
    ref: ref,
    className: clsx('components-font-size-picker', styles.container)
    // The fieldset needs to be labeled.
    ,
    "aria-labelledby": labelId,
    children: [/*#__PURE__*/_jsx(Spacer, {
      children: /*#__PURE__*/_jsxs(HStack, {
        className: clsx('components-font-size-picker__header', styles.header),
        children: [/*#__PURE__*/_jsx(BaseControl.VisualLabel, {
          id: labelId,
          className: styles['header-label'],
          children: __('Font size')
        }), !disableCustomFontSizes && /*#__PURE__*/_jsx(Button, {
          className: styles['header-toggle'],
          label: currentPickerType === 'custom' ? __('Use size preset') : __('Set custom size'),
          icon: settings,
          onClick: () => setUserRequestedCustom(!userRequestedCustom),
          isPressed: currentPickerType === 'custom',
          size: "small"
        })]
      })
    }), /*#__PURE__*/_jsxs("div", {
      children: [currentPickerType === 'select' && /*#__PURE__*/_jsx(FontSizePickerSelect, {
        fontSizes: fontSizes,
        value: value,
        valueMode: valueMode,
        disableCustomFontSizes: disableCustomFontSizes,
        onChange: (newValue, selectedItem) => {
          if (newValue === undefined) {
            onChange?.(undefined, selectedItem);
          } else {
            onChange?.(hasUnits ? newValue : Number(newValue), selectedItem);
          }
        },
        onSelectCustom: () => setUserRequestedCustom(true)
      }), currentPickerType === 'togglegroup' && /*#__PURE__*/_jsx(FontSizePickerToggleGroup, {
        fontSizes: fontSizes,
        value: value,
        valueMode: valueMode,
        onChange: (newValue, selectedItem) => {
          if (newValue === undefined) {
            onChange?.(undefined, selectedItem);
          } else {
            onChange?.(hasUnits ? newValue : Number(newValue), selectedItem);
          }
        }
      }), currentPickerType === 'custom' && /*#__PURE__*/_jsxs(Flex, {
        className: "components-font-size-picker__custom-size-control",
        children: [/*#__PURE__*/_jsx(FlexItem, {
          isBlock: true,
          children: /*#__PURE__*/_jsx(UnitControl, {
            label: __('Font size'),
            labelPosition: "top",
            hideLabelFromVision: true,
            value: hasUnits ? `${valueQuantity ?? ''}${valueUnit ?? ''}` : resolvedValueForControls,
            onChange: newValue => {
              setUserRequestedCustom(true);

              // Treat clearing the input (empty string) as a reset
              if (newValue === undefined || newValue === '') {
                onChange?.(undefined);
              } else {
                onChange?.(hasUnits ? newValue : parseInt(newValue, 10));
              }
            },
            units: hasUnits ? units : [],
            min: 0
          })
        }), withSlider && /*#__PURE__*/_jsx(FlexItem, {
          isBlock: true,
          children: /*#__PURE__*/_jsx(Spacer, {
            marginX: 2,
            marginBottom: 0,
            children: /*#__PURE__*/_jsx(RangeControl, {
              className: "components-font-size-picker__custom-input",
              label: __('Font size'),
              hideLabelFromVision: true,
              value: valueQuantity,
              initialPosition: fallbackFontSize,
              withInputField: false,
              onChange: newValue => {
                setUserRequestedCustom(true);
                if (newValue === undefined) {
                  onChange?.(undefined);
                } else if (hasUnits) {
                  onChange?.(newValue + (valueUnit ?? 'px'));
                } else {
                  onChange?.(newValue);
                }
              },
              min: 0,
              max: isValueUnitRelative ? 10 : 100,
              step: isValueUnitRelative ? 0.1 : 1
            })
          })
        }), withReset && /*#__PURE__*/_jsx(FlexItem, {
          children: /*#__PURE__*/_jsx(Button, {
            disabled: isDisabled,
            accessibleWhenDisabled: true,
            onClick: () => {
              onChange?.(undefined);
            },
            variant: "secondary",
            __next40pxDefaultSize: true,
            size: "default",
            children: __('Reset')
          })
        })]
      })]
    })]
  });
};
export const FontSizePicker = forwardRef(UnforwardedFontSizePicker);
FontSizePicker.displayName = 'FontSizePicker';
export default FontSizePicker;